import { FormalVoucherHeader, TemporaryVoucherHeader, VoucherLine } from '../../services/chain29VoucherApi';
import { AcceptanceFormulaEvidence, AcceptanceManifest } from './types';

interface FormulaContext {
  kind?: 'temporary' | 'formal';
  details?: Array<Pick<VoucherLine, 'side' | 'amount'>>;
  apiHeader?: Partial<TemporaryVoucherHeader & FormalVoucherHeader>;
  databaseHeader?: Partial<TemporaryVoucherHeader & FormalVoucherHeader>;
  lastMutationResponse?: unknown;
}

const cents = (value: number) => Math.round(Number(value || 0) * 100);

const totals = (details: FormulaContext['details']) => (details || []).reduce(
  (sum, line) => ({
    debit: sum.debit + (String(line.side).toUpperCase() === 'D' ? cents(line.amount) : 0),
    credit: sum.credit + (String(line.side).toUpperCase() === 'C' ? cents(line.amount) : 0),
  }),
  { debit: 0, credit: 0 },
);

export const buildChain29FormulaEvidence = (
  manifest: AcceptanceManifest,
  context: FormulaContext,
): AcceptanceFormulaEvidence[] => manifest.formulas.map(formula => {
  const isFormalBalance = formula.formulaId === 'C29-FORMULA-FORMAL-BALANCE' && context.kind === 'formal';
  const isTemporaryBalance = formula.formulaId === 'C29-FORMULA-TEMP-BALANCE' && context.kind === 'temporary';
  if (!isFormalBalance && !isTemporaryBalance) {
    return {
      formulaId: formula.formulaId,
      status: 'not_observed',
      detail: '本次畫面尚未執行或尚無 DB 證據；不得視為 PASS。',
    };
  }

  const calculated = totals(context.details);
  const apiDebit = cents(Number(context.apiHeader?.debitTotal || 0));
  const apiCredit = cents(Number(context.apiHeader?.creditTotal || 0));
  const dbDebit = cents(Number(context.databaseHeader?.debitTotal || 0));
  const dbCredit = cents(Number(context.databaseHeader?.creditTotal || 0));
  const hasApiAndDb = !!context.apiHeader && !!context.databaseHeader;
  const balanced = calculated.debit === calculated.credit && calculated.debit > 0;
  const agrees = hasApiAndDb
    && calculated.debit === apiDebit
    && calculated.credit === apiCredit
    && apiDebit === dbDebit
    && apiCredit === dbCredit;

  return {
    formulaId: formula.formulaId,
    status: hasApiAndDb ? (balanced && agrees ? 'pass' : 'fail') : 'not_observed',
    inputs: context.details,
    intermediateValues: {
      debit: calculated.debit / 100,
      credit: calculated.credit / 100,
      difference: (calculated.debit - calculated.credit) / 100,
    },
    apiResult: context.apiHeader || context.lastMutationResponse,
    databaseResult: context.databaseHeader,
    detail: !hasApiAndDb
      ? '已計算畫面預覽，但尚缺 API／DB 三方證據。'
      : balanced && agrees
        ? '畫面計算、API 合計與 DB 即時合計一致。'
        : '畫面、API 或 DB 合計不一致，或借貸不平衡。',
  };
});
